import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Calendar, ArrowLeft, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function Subscription() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/subscription").then((res) => setSub(res.data));
  useEffect(() => {
    load();
  }, []);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Abo gekündigt – VIP bleibt bis zum Ablaufdatum aktiv");
      await load();
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kündigung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <button
          data-testid="sub-back"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-all"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>

        <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mb-1">Mein Abo</h1>
        <p className="text-slate-400 text-sm mb-8">Verwalte deine VIP-Mitgliedschaft</p>

        {!sub ? (
          <div className="h-40 rounded-3xl bg-white/5 animate-pulse" />
        ) : sub.is_owner ? (
          <div className="rounded-3xl bg-[#12141D] border border-amber-400/30 p-8 text-center" data-testid="sub-owner">
            <Crown className="w-10 h-10 text-amber-300 mx-auto" />
            <h2 className="font-display text-xl font-bold text-white mt-4">Owner-Konto</h2>
            <p className="text-slate-400 text-sm mt-2">Du hast dauerhaften, unbegrenzten Zugriff auf alles.</p>
          </div>
        ) : sub.is_vip ? (
          <div className="rounded-3xl bg-gradient-to-b from-[#1A1327] to-[#12141D] border border-fuchsia-500/30 p-8" data-testid="sub-active">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-fuchsia-600 flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-white">{sub.plan}</h2>
                <p className="text-fuchsia-300 text-sm font-semibold">{sub.price}</p>
              </div>
              <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
                <ShieldCheck className="w-4 h-4" /> Aktiv
              </span>
            </div>

            <div className="mt-6 flex items-center gap-2 text-slate-300 text-sm bg-white/5 rounded-xl px-4 py-3">
              <Calendar className="w-4 h-4 text-slate-400" />
              {sub.cancelled ? (
                <span data-testid="sub-expiry">
                  Läuft aus am <span className="text-white font-semibold">{fmtDate(sub.vip_until)}</span> – keine Verlängerung
                </span>
              ) : (
                <span data-testid="sub-renew">
                  Verlängert sich am <span className="text-white font-semibold">{fmtDate(sub.vip_until)}</span>
                </span>
              )}
            </div>

            {sub.cancelled ? (
              <div className="mt-6 flex items-center gap-2 text-amber-300 text-sm">
                <XCircle className="w-4 h-4" /> Dein Abo wurde gekündigt und endet automatisch.
              </div>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    data-testid="sub-cancel-btn"
                    disabled={busy}
                    className="mt-6 w-full bg-white/5 hover:bg-rose-500/10 border border-white/10 hover:border-rose-400/40 text-slate-300 hover:text-rose-300 font-medium rounded-xl py-3 transition-all disabled:opacity-60"
                  >
                    Abo kündigen
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="glass border-white/10 text-slate-200">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Abo wirklich kündigen?</AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-400">
                      Dein VIP-Zugang bleibt bis zum {fmtDate(sub.vip_until)} aktiv. Danach wird nicht mehr
                      verlängert und du verlierst den Zugriff auf VIP-Charaktere und unbegrenzte Nachrichten.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-white/5 border-white/10 text-slate-200 hover:bg-white/10">
                      Behalten
                    </AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="sub-cancel-confirm"
                      onClick={cancel}
                      className="bg-rose-600 hover:bg-rose-500 text-white"
                    >
                      Ja, kündigen
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ) : (
          <div className="rounded-3xl bg-[#12141D] border border-white/10 p-8 text-center" data-testid="sub-none">
            <Crown className="w-10 h-10 text-slate-600 mx-auto" />
            <h2 className="font-display text-xl font-bold text-white mt-4">Kein aktives Abo</h2>
            <p className="text-slate-400 text-sm mt-2 mb-6">Werde VIP für unbegrenzte Chats und exklusive Charaktere.</p>
            <button
              onClick={() => navigate("/vip")}
              className="bg-gradient-to-r from-amber-500 via-fuchsia-600 to-pink-600 text-white font-bold rounded-full px-6 py-3 active:scale-95 transition-all"
            >
              Jetzt VIP werden
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
